import { useState } from 'react';

import { useQuery } from '@apollo/client';
import { t } from 'i18next';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

import { StringMapDropdown } from '@/components';
import { GET_ALL_SHIPMENT_GROUP_MASTERS } from '@/types.gql';

import { CreateShipmentGroupMasterDialog } from '../CreateShipmentGroupMasterDialog';

export function ViewShipmentGroupDialog({
  visible,
  onHide,
  shipmentGroupId,
  shipmentGroupName,
  shipmentGroupEstimations,
}: {
  visible: boolean;
  onHide: () => void;
  shipmentGroupId?: string;
  shipmentGroupName?: string;
  shipmentGroupEstimations?: number;
}) {
  const [showAddShipmentGroupMaster, setShowAddShipmentGroupMaster] =
    useState(false);

  const { data: shipmentGroupMasters, refetch: refetchShipmentGroupMasters } =
    useQuery(GET_ALL_SHIPMENT_GROUP_MASTERS, {
      variables: {
        where: shipmentGroupId
          ? { shipmentGroupId: { eq: shipmentGroupId } }
          : {},
      },
      skip: !shipmentGroupId,
    });

  const handlePlusClick = () => {
    setShowAddShipmentGroupMaster(true);
  };

  const handleShipmentGroupMasterSave = async () => {
    setShowAddShipmentGroupMaster(false);
    await refetchShipmentGroupMasters();
  };

  const shipmentGroupMastersData =
    shipmentGroupMasters?.allShipmentGroupMasters?.items || [];

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t('shipping.viewShipmentGroup')}
      footer={
        <>
          <Button label="Close" onClick={onHide} />
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label>{t('common.name')}</label>
          <InputText
            value={shipmentGroupName || ''}
            disabled
            className="bg-gray-100"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label>{t('shipping.estimations')}</label>
          <StringMapDropdown
            className="w-full"
            disabled
            objectTypeCode={10122}
            attributeName="tog_shipmentestimations"
            value={shipmentGroupEstimations || 0}
            onChange={() => {}}
          />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              Shipment Group Masters
            </h3>
            <Button
              icon="pi pi-plus"
              className="p-button-sm p-button-outlined"
              tooltip="Add Shipment Group Master"
              onClick={handlePlusClick}
            />
          </div>

          <DataTable
            value={shipmentGroupMastersData}
            scrollable
            scrollHeight="200px"
            className="rounded-lg border border-gray-200"
            emptyMessage="No shipment group masters found"
          >
            <Column
              field="name"
              header="Name"
              className="font-medium"
              style={{ minWidth: '150px' }}
            />
            <Column
              field="site.name"
              header="Site"
              style={{ minWidth: '100px' }}
            />
            <Column
              field="contact.fullName"
              header="Contact"
              style={{ minWidth: '120px' }}
            />
          </DataTable>
        </div>
      </div>
      <CreateShipmentGroupMasterDialog
        visible={showAddShipmentGroupMaster}
        onHide={() => setShowAddShipmentGroupMaster(false)}
        shipmentGroupId={shipmentGroupId || ''}
        afterSave={handleShipmentGroupMasterSave}
      />
    </Dialog>
  );
}
